import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { PowerSyncContext, useQuery, useStatus } from "@powersync/react";
import type { PowerSyncDatabase } from "@powersync/web";
import { EctConnector, MediaQueue, connectEctDatabase, openEctDatabase, opfsBlobStore, pendingUploads, pillState, watchConnectivity, type Actor, type PillState } from "@ect/core";
import { useSession } from "./session";

interface SyncCtx { db: PowerSyncDatabase; media: MediaQueue; actor: Actor; online: boolean; rejected: number }
const Ctx = createContext<SyncCtx | null>(null);

/**
 * Opens the per-user local replica, connects PowerSync through the API connector and runs the media queue.
 * Reads go to SQLite through useQuery; writes go to SQLite and are uploaded by the connector (ADR 003).
 */
export function SyncProvider({ children }: { children: ReactNode }) {
  const { api, session } = useSession();
  const userId = session!.user.id;
  const actor = useMemo<Actor>(() => ({ userId, deviceId: api.deviceId }), [userId, api]);
  const db = useMemo(() => openEctDatabase({ dbFilename: `ect-${userId}.sqlite` }), [userId]);
  const [rejected, setRejected] = useState(0);
  const media = useMemo(() => new MediaQueue(db, opfsBlobStore(), { uploadUrl: (id) => api.json(`/api/attachments/${id}/upload-url`), put: (url, init) => (url.startsWith("/") ? api.fetch(url, init) : fetch(url, init)) }, actor), [db, api, actor]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const connector = new EctConnector(api, (r) => setRejected((n) => n + r.length));
    void connectEctDatabase(db, connector).catch((e) => console.error("[sync] connect failed", e));
    const stop = watchConnectivity((c) => { setOnline(c.online); if (c.online) void media.drain(); });
    const timer = setInterval(() => void media.drain(), 30_000);
    return () => { stop(); clearInterval(timer); void db.disconnectAndClear({ clearLocal: false }).catch(() => undefined); };
  }, [db, api, media]);
  return (
    <PowerSyncContext.Provider value={db}>
      <Ctx.Provider value={{ db, media, actor, online, rejected }}>{children}</Ctx.Provider>
    </PowerSyncContext.Provider>
  );
}
export function useSync(): SyncCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("SyncProvider missing");
  return c;
}

/** SyncPill state + pending count for the top bar. */
export function useSyncPill(): { state: PillState; pending: number; lastSyncedAt: Date | null } {
  const status = useStatus();
  const { online } = useSync();
  const { data } = useQuery<{ n: number }>("select count(*) as n from ps_crud", []);
  const pending = Number(data?.[0]?.n ?? 0);
  return { state: pillState(status, pending, online), pending, lastSyncedAt: status?.lastSyncedAt ?? null };
}
export { pendingUploads };
